import React, { useContext, useState } from "react";
import { useHistory, Redirect } from 'react-router-dom';
import { useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import { Card, Row} from "react-bootstrap"
import { DirectionalHint, TooltipHost, Dialog, DialogType, DialogFooter } from '@fluentui/react';
import { CommandBarButton, PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { OverflowSet } from '@fluentui/react/lib/OverflowSet';
import List from '../../components/Controls/List';
import PropTypes from 'prop-types';

import {useGetUserTableSchema} from "../../hooks/useOfListUser";

import "../../css/CustomButton.css"
import AuthVerify from "../../components/AuthVerify";
import MainAppContext from "../../MainAppContext";
import { useGetProfiles } from "../../hooks/useOfManageUserPermission";
import { isNullUndefinedOrEmpty } from "../../common/Helpers";
import userService from "../../services/user.service";
import authService from "../../services/auth.service";
import { __ProfileName } from "../../common/Dictionaries";

function ListUsers(props) {
    const { t } = useTranslation();
    const history = useHistory();
  
    const { user: currentAuth } = useSelector((state) => state.auth);
    const contextSite = useContext(MainAppContext);

    const currentUserClientId = currentAuth !== null ? currentAuth.clientId : null;
    const client_id_aux = props.location.state?.clientId ?? currentUserClientId;

    const [usersData, setUsersData] = useState({data: null, pageNumber: 1, pageSize: 10, filters: []});
    useGetUserTableSchema(client_id_aux, usersData, setUsersData);

    const [userTypeList, setUserTypeList] = useState(null);
    useGetProfiles(userTypeList, setUserTypeList, false)

    const isAdminOrSAdmin = currentAuth?.profile?.some(
        p => p === __ProfileName.Admin || p === __ProfileName.sAdmin
    );

    const [revokeDialog, setRevokeDialog] = useState({ open: false, userId: null, username: '' });
    const [revokeStatus, setRevokeStatus] = useState(null);

    const handleRevokeClick = (item) => {
        setRevokeStatus(null);
        setRevokeDialog({ open: true, userId: item.id, username: item.username });
    };

    const handleRevokeConfirm = async () => {
        try {
            await authService.revokeUserSessions(revokeDialog.userId);
            setRevokeStatus('success');
            setTimeout(() => setRevokeDialog({ open: false, userId: null, username: '' }), 1500);
        } catch (err) {
            setRevokeStatus('error');
        }
    };

    const handleRevokeCancel = () => {
        setRevokeDialog({ open: false, userId: null, username: '' });
        setRevokeStatus(null);
    };

    const onRenderItemStyles = { root: { padding: '10px' } };
    const onRenderOverflowButtonStyles = { root: { padding: '10px' }, menuIcon: { fontSize: '16px' } };

    const handleOptionClick = (item, url) => {
        history.push(url, { userName: item.username })
    }
    
    const onRenderOverflowButton = (overflowItems) => {
        return (
          <TooltipHost content="More items" directionalHint={DirectionalHint.rightCenter}>
            <CommandBarButton
              role="menuitem"
              aria-label="More items"
              styles={onRenderOverflowButtonStyles}
              menuIconProps={{ iconName: 'More' }}
              menuProps={{ items: overflowItems }}
            />
          </TooltipHost>
        );
    };
    const onRenderItem = (item) => {
        return (
          <TooltipHost content={item.title} directionalHint={DirectionalHint.rightCenter}>
            <CommandBarButton
              role="menuitem"
              aria-label={item.name}
              styles={onRenderItemStyles}
              iconProps={{ iconName: item.icon }}
              onClick={item.onClick}
            />
          </TooltipHost>
        );
    };

    const renderActionColumn = (item) => {
        let showChangePass = null;
        
        if (item.formBasedAuthEnabled === null && item.azureActiveDirectoryAuthEnabled === null) {
            showChangePass = true
        }
        else if(item.formBasedAuthEnabled) {
            showChangePass = true
        }
        else if(item.azureActiveDirectoryAuthEnabled) {
            showChangePass = false
        }
        else {
            showChangePass = true
        }

        return <div>
                <OverflowSet
                    className="action-menu-list-toolbar d-flex justify-content-center list-menu-action"
                    role="menubar"
                    items={[
                        {
                            key: 'item1',
                            icon: 'NavigateBackMirrored',
                            name: t('data-lang-pagename-profile'),
                            title: t('data-lang-pagename-profile'),
                            onClick: () => handleOptionClick(item, "/profile"),
                        },
                        {
                            key: 'item2',
                            icon: 'EditContact',
                            name: t('data-lang-pagename-edituser'),
                            title: t('data-lang-pagename-edituser'),
                            onClick: () => handleOptionClick(item, "/edituser"),
                        },
                        {
                            key: 'item3',
                            icon: 'Encryption',
                            name: t('data-lang-pagename-changepassword'),
                            title: t('data-lang-pagename-changepassword'),
                            onClick: () => handleOptionClick(item, "/changepassword"),
                        },
                        {
                            key: 'itemRevoke',
                            icon: 'BlockContact',
                            name: t('data-lang-btn-revoke-session'),
                            title: t('data-lang-btn-revoke-session'),
                            onClick: () => handleRevokeClick(item),
                        },
                    ].filter(x => x.key !== "item3" || showChangePass)
                     .filter(x => x.key !== "itemRevoke" || (isAdminOrSAdmin && item.username !== currentAuth?.userName))
                     .filter(x => contextSite.onlyObserver !== true)}
                    onRenderOverflowButton={onRenderOverflowButton}
                    onRenderItem={onRenderItem}
                />
        </div>
    }
    const renderLegalEntityColumn = (item) => {
        return  <div>
                    <div><b>{t('data-lang-lbl-legalentity')}</b>: {item.legalEntityName}</div>
                    <div><b>{t('data-lang-lbl-businessunit')}</b>: {item.businessUnitName}</div>
                </div>
    }

    const renderUserNameColumn = (item) => {
        const userType = userTypeList.find(x => x.id === item.userTypeId);
        return  <div>
                    {
                        <>
                            <div>
                                { item.username }
                                {
                                    item.isAdmin === true ? 
                                        <TooltipHost content={t('data-lang-lbl-isadmin')} directionalHint={DirectionalHint.rightCenter}>
                                            <i className="ms-Icon ms-Icon--StarburstSolid" style={{color: "orange"}} aria-hidden="true"></i>
                                        </TooltipHost>
                                    :
                                        <></>
                                }
                            </div>
                            <div>
                                <b>{t('data-lang-lbl-enabled')}</b>:&nbsp;
                                <>
                                {
                                    
                                    !isNullUndefinedOrEmpty(item.lockoutType) &&
                                    <>
                                        {
                                            (!item.isLockedOut ? t('data-lang-lbl-yes') : (<span>{ `${t('data-lang-lbl-no')} (${t(item.lockoutType)})` }</span> ))
                                        }
                                    </>
                                }
                                </>
                                <>
                                {
                                    isNullUndefinedOrEmpty(item.lockoutType) &&
                                    <>
                                        {
                                            (item.isLockedOut ? t('data-lang-lbl-no') : (<span>{ t('data-lang-lbl-yes') }</span> ))
                                        }
                                    </>
                                }
                                </>
                            </div>
                            <div><b>{t('data-lang-lbl-usertype')}</b>: { userType ? userType.name : '--' }</div>
                        </>
                    }
                </div>
    }

    const handleExport = (handleDownload, listField) => {  
        userService.getAllUsersExportExcel(client_id_aux, JSON.parse(decodeURIComponent(listField))).then((serviceResponse) => {
            handleDownload(serviceResponse.data, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,");
        });
    };

    const _columns = [
        {
            key: 'username',
            name: t('data-lang-lbl-username'),
            fieldName: 'username',
            minWidth: 150,
            maxWidth: 250,
            onRender:(item) => renderUserNameColumn(item)
        },
        {
            key: 'legalEntityName',
            name: t('data-lang-lbl-businessunit'),
            fieldName: 'businessunit',
            minWidth: 200,
            maxWidth: 400,
            onRender:(item) => renderLegalEntityColumn(item)
        },               
        {  
            key: 'firstName',
            name: t('data-lang-lbl-firstname'),
            fieldName: 'firstName',
            minWidth: 100,
            maxWidth: 200,
        },
        {  
            key: 'lastName',
            name: t('data-lang-lbl-lastname'),
            fieldName: 'lastName',
            minWidth: 100,
            maxWidth: 200,
        },
        {  
            key: 'phoneNumber',
            name: t('data-lang-lbl-phonenumber'),
            fieldName: 'phoneNumber',
            minWidth: 150,
            maxWidth: 150,
        },
        {  
            key: 'email',
            name: t('data-lang-lbl-email'),
            fieldName: 'email',
            minWidth: 250,
            maxWidth: 250,
        },
        {  
            key: 'id',
            name: t('data-lang-lbl-actions'),
            fieldName: 'actions',
            minWidth: 170,
            maxWidth: 170,
            onRender:(item) => renderActionColumn(item)
        }
    ];

    const _fieldsToExport = [
        {
            key: 'username',
            name: t('data-lang-lbl-username'),
            fieldName: 'username',
        },
        {  
            key: 'firstName',
            name: t('data-lang-lbl-firstname'),
            fieldName: 'firstName',
        },
        {  
            key: 'lastName',
            name: t('data-lang-lbl-lastname'),
            fieldName: 'lastName',
        },
        {
            key: 'legalEntityName',
            name: t('data-lang-lbl-legalentity'),
            fieldName: 'legalEntityName',
        },   
        {
            key: 'businessUnitName',
            name: t('data-lang-lbl-businessunit'),
            fieldName: 'businessUnitName',
        },             
        {  
            key: 'phoneNumber',
            name: t('data-lang-lbl-phonenumber'),
            fieldName: 'phoneNumber',
        },
        {  
            key: 'email',
            name: t('data-lang-lbl-email'),
            fieldName: 'email',
        },
        {  
            key: 'userTypeName',
            name: t('data-lang-lbl-accounttype'),
            fieldName: 'userTypeName',
        },
        {  
            key: 'isAdmin',
            name: t('data-lang-lbl-isadmin'),
            fieldName: 'isAdmin',
        },
        {  
            key: 'isLockedOut',
            name: t('data-lang-lbl-disabled'),
            fieldName: 'isLockedOut',
        },
        {  
            key: 'lockoutType',
            name: t('data-lang-lbl-lockouttype'),
            fieldName: 'lockoutType',
        },
        {  
            key: 'lastSignIn',
            name: t('data-lang-lbl-lastsignin'),
            fieldName: 'lastSignIn',
        },
        {  
            key: 'isLockedOutByLastSignin',
            name: t('data-lang-lbl-islockedoutbylastsignin'),
            fieldName: 'isLockedOutByLastSignin',
        }
    ];

    return (
        <Row>
            <AuthVerify currentToken={currentAuth} />
            <Dialog
                hidden={!revokeDialog.open}
                onDismiss={handleRevokeCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: t('data-lang-btn-revoke-session'),
                    subText: `${t('data-lang-msg-revoke-session-confirm')} ${revokeDialog.username}?`,
                }}
            >
                {revokeStatus === 'success' && (
                    <p style={{ color: 'green', margin: '0 0 12px' }}>{t('data-lang-msg-revoke-session-success')}</p>
                )}
                {revokeStatus === 'error' && (
                    <p style={{ color: 'red', margin: '0 0 12px' }}>{t('data-lang-msg-revoke-session-error')}</p>
                )}
                <DialogFooter>
                    <PrimaryButton
                        onClick={handleRevokeConfirm}
                        text={t('data-lang-btn-confirm')}
                        disabled={revokeStatus === 'success'}
                    />
                    <DefaultButton onClick={handleRevokeCancel} text={t('data-lang-btn-cancel')} />
                </DialogFooter>
            </Dialog>
            <Card  className="rounded-3">
                <Card.Header>
                    <h4>{t('data-lang-pagename-listusers')}
                    </h4>
                </Card.Header>
                <Card.Body className="col-xs-12">
                    <Row>
                        {
                            userTypeList &&
                            <List setData={setUsersData} data={usersData} columns={_columns} fieldsToExport={_fieldsToExport} exportFileName="ClientUsers.xlsx" enableExport={true} handleExport={handleExport} />
                        }
                    </Row>
                </Card.Body>
            </Card>
        </Row>
    );
}
ListUsers.propTypes = {
    location: PropTypes.object.isRequired,
};
export default ListUsers